/**
 * Операции над задачами, выходящие за рамки простого CRUD.
 *
 * Здесь живёт поведение, которое влияет на несколько экранов сразу:
 * закрытие задачи с наградой, автоматический перенос незакрытого на завтра,
 * постановка задачи в свободный слот календаря.
 */

import { db } from '@/shared/db/database';
import type { Task } from '@/shared/db/schema';
import { getCurrentUserId } from '@/shared/db/session-context';
import { createId } from '@/shared/lib/id';
import { addDays, diffDays, type IsoDate, type MinutesOfDay, today } from '@/shared/lib/datetime';

import { isRecurring, nextDateAfterCompletion, ruleOf } from './recurrence';
import { tasksRepo } from './repository';

/** Активные (незакрытые и неудалённые) задачи на дату. */
export async function getTasksForDate(date: IsoDate): Promise<Task[]> {
  const userId = getCurrentUserId();
  if (!userId) return [];

  const tasks = await db.tasks.where('[userId+plannedDate]').equals([userId, date]).toArray();
  return tasks.filter((task) => task.deletedAt === null);
}

/**
 * Отмечает задачу выполненной или возвращает в работу.
 * Возвращает `true`, если задача только что закрыта — вызывающий код по этому
 * признаку решает, показывать ли поздравление.
 */
export async function toggleTaskDone(task: Task): Promise<boolean> {
  const willComplete = task.status !== 'done';

  // Отметка и порождение следующего экземпляра — одна транзакция. Иначе два
  // быстрых нажатия по повторяющейся задаче успевают оба увидеть её незакрытой
  // и создают два экземпляра следующего дня; уникального ключа, который бы это
  // поймал, здесь нет — дубли просто оседают в календаре.
  await db.transaction('rw', db.tasks, db.outbox, async () => {
    const current = await db.tasks.get(task.id);
    if (!current || current.deletedAt !== null) return;
    if ((current.status !== 'done') !== willComplete) return;

    await tasksRepo.update(task.id, {
      status: willComplete ? 'done' : 'todo',
      completedAt: willComplete ? Date.now() : null,
    });

    // Возврат задачи в работу ничего не создаёт: иначе отмена случайного
    // нажатия оставляла бы за собой лишнюю копию.
    if (willComplete) await spawnNextOccurrence(current);
  });

  return willComplete;
}

/**
 * Создаёт следующий экземпляр повторяющейся задачи.
 *
 * Копируется всё, что описывает саму задачу и её место в расписании:
 * название, сфера, оценка, правило повторения и слот в сетке дня.
 * Сбрасывается только то, что относится к конкретному выполнению: статус,
 * отметка о завершении и счётчик переносов.
 *
 * Экземпляры одной цепочки связаны общим `seriesId` — по нему можно найти
 * всю серию, не разбирая правило заново.
 */
async function spawnNextOccurrence(task: Task): Promise<Task | null> {
  if (!isRecurring(task)) return null;

  const completedDate = today();
  const nextDate = nextDateAfterCompletion(ruleOf(task), task.plannedDate, completedDate);
  if (nextDate === null) return null;

  // Первый экземпляр цепочки создавался как обычная задача и `seriesId` не
  // имел. Проставляем его сразу обоим — иначе начало серии осталось бы вне
  // выборки «все повторения этой задачи».
  const seriesId = task.seriesId ?? createId();
  if (task.seriesId === null) {
    await tasksRepo.update(task.id, { seriesId });
  }

  return tasksRepo.create({
    title: task.title,
    notes: task.notes,
    sphereId: task.sphereId,
    goalId: task.goalId,
    status: 'todo',
    priority: task.priority,
    estimateMin: task.estimateMin,
    plannedDate: nextDate,
    // Слот в сетке переносится вместе с задачей: повторяющиеся дела почти
    // всегда привязаны к одному и тому же времени (зарядка в 7:00, созвон по
    // понедельникам в 11:00), и заново расставлять их каждый раз — лишняя
    // работа. Если в новом дне это время окажется занято, наложение видно
    // в календаре и правится перетаскиванием.
    plannedStartMin: task.plannedStartMin,
    // Дедлайн сдвигается вместе с задачей, сохраняя исходный зазор до срока.
    dueDate: shiftDueDate(task, nextDate),
    dueMin: task.dueMin,
    completedAt: null,
    rolloverCount: 0,
    originalDate: nextDate,
    habitId: task.habitId,
    position: task.position,
    recurrence: task.recurrence,
    recurrenceInterval: task.recurrenceInterval,
    recurrenceWeekdays: task.recurrenceWeekdays,
    recurrenceFrom: task.recurrenceFrom,
    recurrenceUntil: task.recurrenceUntil,
    seriesId,
  });
}

/**
 * Переносит дедлайн на новый экземпляр, сохраняя зазор между планом и сроком.
 * Например, «сделать 28-го, крайний срок 30-го» → «сделать 31-го, срок 2-го».
 */
function shiftDueDate(task: Task, nextDate: IsoDate): IsoDate | null {
  if (task.dueDate === null) return null;
  if (task.plannedDate === null) return nextDate;

  const gap = diffDays(task.plannedDate, task.dueDate);
  return gap === 0 ? nextDate : addDays(nextDate, gap);
}

/** Ставит задачу на конкретное время конкретного дня. */
export async function scheduleTask(
  taskId: string,
  date: IsoDate,
  startMin: MinutesOfDay | null,
): Promise<void> {
  const task = await tasksRepo.get(taskId);
  if (!task) return;

  await tasksRepo.update(taskId, {
    plannedDate: date,
    plannedStartMin: startMin,
    // Дата первого планирования фиксируется один раз: по ней видно,
    // как давно задача кочует по календарю.
    originalDate: task.originalDate ?? date,
  });
}

/** Убирает задачу из календаря обратно в общий список. */
export async function unscheduleTask(taskId: string): Promise<void> {
  await tasksRepo.update(taskId, { plannedDate: null, plannedStartMin: null });
}

export interface RolloverResult {
  movedCount: number;
  /** Сколько из них забрано по прошедшему дедлайну, а не перенесено со вчера. */
  claimedCount: number;
  /** Задачи, которые переносятся уже не первый раз, — кандидаты на пересмотр. */
  stuckTasks: Task[];
}

/**
 * Собирает в сегодняшний день всё незакрытое, чему больше негде быть.
 *
 * Запускается один раз при старте приложения. Смысл — не дать задаче потеряться
 * в прошлом: невыполненное вчера остаётся видимым сегодня.
 *
 * Забираются два разных случая, и оба по одной причине — иначе задача видна в
 * сегодняшнем дне, но ни в одно его число не входит:
 *
 * 1. День выполнения прошёл. Обычный перенос: счётчик `rolloverCount` растёт,
 *    и задача с большим счётчиком показывается в интерфейсе особо — обычно это
 *    признак, что её пора либо разбить на части, либо честно отменить.
 *
 * 2. Дедлайн прошёл, а дня у задачи нет вовсе. Откладывать больше некуда, так
 *    что день становится сегодняшним. Счётчик переносов при этом не трогается:
 *    задачу никто не переносил, она впервые попадает в день, и приписывать ей
 *    чужую историю нельзя — по этому счётчику человек решает, что пересмотреть.
 *
 * Задачу, запланированную на будущий день, перенос не трогает даже с
 * просроченным дедлайном: день выбран человеком, и она уже сосчитана в своём
 * дне. Переписать этот выбор значило бы решить за него.
 *
 * Выбранное время сохраняется: если человек сознательно поставил задачу на
 * 9:00, она и завтра нужна ему в 9:00. Сбрасывать слот значило бы заставлять
 * планировать заново то, что уже спланировано; возможное наложение видно в
 * сетке дня и правится перетаскиванием.
 */
export async function rolloverOverdueTasks(targetDate: IsoDate = today()): Promise<RolloverResult> {
  const empty: RolloverResult = { movedCount: 0, claimedCount: 0, stuckTasks: [] };
  const userId = getCurrentUserId();
  if (!userId) return empty;

  /*
    Один запрос по индексу `[userId+status]` на оба случая.

    Раньше запрос шёл по `[userId+status+plannedDate]` и был уже — только
    задачи с прошедшим днём. Но задача без дня в этот индекс не попадает вовсе:
    IndexedDB не хранит составной ключ с пустой частью, — а именно её и нужно
    забрать по дедлайну. Заводить ради этого второй индекс и миграцию базы
    накладнее, чем один раз при старте прочитать список открытых задач: он
    ограничен тем, что человек реально держит незакрытым, а выполненные за годы
    в него не попадают.
  */
  const open = (await db.tasks.where('[userId+status]').equals([userId, 'todo']).toArray()).filter(
    (task) => task.deletedAt === null,
  );

  const overdue = open.filter(
    (task) => task.plannedDate !== null && task.plannedDate < targetDate,
  );

  const claimed = open.filter(
    (task) => task.plannedDate === null && task.dueDate !== null && task.dueDate < targetDate,
  );

  if (overdue.length === 0 && claimed.length === 0) return empty;

  // Одна транзакция на весь перенос: счётчик переносов читается из записи, и
  // два параллельных запуска (две вкладки, повторный монтаж экрана) увеличивали
  // его дважды за один перенос. Заодно это одна транзакция вместо сотни.
  await db.transaction('rw', db.tasks, db.outbox, async () => {
    for (const task of overdue) {
      const current = await db.tasks.get(task.id);
      if (!current || current.plannedDate === null || current.plannedDate >= targetDate) continue;

      await tasksRepo.update(task.id, {
        plannedDate: targetDate,
        rolloverCount: current.rolloverCount + 1,
      });
    }

    for (const task of claimed) {
      const current = await db.tasks.get(task.id);
      if (!current || current.plannedDate !== null) continue;

      await tasksRepo.update(task.id, {
        plannedDate: targetDate,
        // День у задачи появился впервые — с него и считается её история.
        originalDate: current.originalDate ?? targetDate,
      });
    }
  });

  return {
    movedCount: overdue.length + claimed.length,
    claimedCount: claimed.length,
    stuckTasks: overdue.filter((task) => task.rolloverCount >= 2),
  };
}

/**
 * Перенос конкретной задачи на завтра — ручное действие пользователя.
 * Время в сетке сохраняется, как и при автоматическом переносе.
 */
export async function postponeToTomorrow(task: Task): Promise<void> {
  const from = task.plannedDate ?? today();
  await tasksRepo.update(task.id, {
    plannedDate: addDays(from, 1),
    rolloverCount: task.rolloverCount + 1,
  });
}

/** Просрочен ли дедлайн задачи. */
export function isOverdue(task: Task, reference: IsoDate = today()): boolean {
  if (task.status === 'done' || task.dueDate === null) return false;
  return task.dueDate < reference;
}

/**
 * День выполнения назначен позже дедлайна — задача заведомо не успевает.
 *
 * Одно правило на оба места, где день выбирают: форма задачи и перенос на
 * завтра. Говорить об этом стало обязательно с тех пор, как отложенная задача
 * уходит из сегодняшнего списка: раньше о просроченном сроке напоминала сама
 * карточка, попадавшаяся на глаза каждый день, а теперь её там нет.
 */
export function isPlannedAfterDue(
  plannedDate: IsoDate | null,
  dueDate: IsoDate | null,
): boolean {
  if (plannedDate === null || dueDate === null) return false;
  return plannedDate > dueDate;
}

/** Дедлайн сегодня или уже прошёл — задача требует внимания. */
export function isUrgent(task: Task, reference: IsoDate = today()): boolean {
  if (task.status === 'done' || task.dueDate === null) return false;
  return task.dueDate <= reference;
}

/** Срок без часа — «до конца дня», то есть позже любого срока с часом. */
const END_OF_DAY_MIN = 24 * 60;

/**
 * Порядок важности: пометка «Важно» → срок → длительность, от короткой к
 * длинной → порядок, заданный пользователем. Время в календаре не участвует.
 *
 * Отдельно от `compareTasks` он нужен там, где список режут с конца: совет
 * «перенесите N задач» должен снимать наименее важное, а не самое позднее.
 */
export function compareByImportance(a: Task, b: Task): number {
  /*
    Ступеней приоритета было три, и в резервной копии годичной давности лежит
    «низкий». Запас на неизвестное значение обязателен: без него вес оказался
    бы `undefined`, сравнение вернуло бы NaN, и список рассыпался бы в
    непредсказуемом порядке — молча, без единой ошибки в журнале.

    Важность решает раньше срока. Раньше было наоборот, и задача с любым
    дедлайном стояла выше важной без него; после переноса задач из другого
    приложения срок оказался у каждой, и пометка «Важно» перестала влиять на
    порядок вовсе. Её ставят ровно затем, чтобы дело было наверху.
  */
  const weightOf = (priority: string) => (priority === 'high' ? 0 : 1);
  if (a.priority !== b.priority) return weightOf(a.priority) - weightOf(b.priority);

  // При равной важности задачи со сроком идут раньше бессрочных.
  if (a.dueDate !== b.dueDate) {
    if (a.dueDate === null) return 1;
    if (b.dueDate === null) return -1;
    return a.dueDate < b.dueDate ? -1 : 1;
  }

  // В один день срок в 10:00 наступает раньше срока «до конца дня».
  const dueA = a.dueMin ?? END_OF_DAY_MIN;
  const dueB = b.dueMin ?? END_OF_DAY_MIN;
  if (dueA !== dueB) return dueA - dueB;

  /*
    Дальше — короткое раньше длинного. Короткое дело закрывается сразу и
    освобождает голову, а длинное всё равно требует выделенного окна.
  */
  if (a.estimateMin !== b.estimateMin) return a.estimateMin - b.estimateMin;

  return a.position - b.position;
}

/**
 * Порядок задач в списке.
 *
 * Закрытые уходят вниз. Задачи без времени идут первыми и выстраиваются по
 * важности (`compareByImportance`) — как на вкладке дня, где «Без времени»
 * стоит выше «В расписании»: им ещё нужно найти место. Задачи со временем
 * идут следом по дню и часу: они уже стоят в календаре, и список, где вечернее
 * дело выше утреннего, читается как ошибка. Важность решает у них только при
 * одном и том же времени.
 *
 * Блоки не перемешиваются намеренно: правило «по часу, если время есть у
 * обеих, иначе по важности» не транзитивно, и сортировка на нём выдавала бы
 * разный порядок для одного и того же списка.
 */
export function compareTasks(a: Task, b: Task): number {
  if (a.status !== b.status) return a.status === 'done' ? 1 : -1;

  const timedA = a.plannedStartMin !== null;
  const timedB = b.plannedStartMin !== null;
  if (timedA !== timedB) return timedA ? 1 : -1;

  if (a.plannedStartMin !== null && b.plannedStartMin !== null) {
    const dayA = a.plannedDate ?? '';
    const dayB = b.plannedDate ?? '';
    if (dayA !== dayB) return dayA < dayB ? -1 : 1;
    if (a.plannedStartMin !== b.plannedStartMin) return a.plannedStartMin - b.plannedStartMin;
  }

  return compareByImportance(a, b);
}

/**
 * Можно ли сделать задачу встречей. Повторяющуюся и дело привычки — нельзя:
 * встреча не повторяется, а дело привычки живёт по её расписанию и вернётся
 * завтра.
 */
export function canBecomeEvent(task: Task): boolean {
  return task.habitId === null && (task.recurrence ?? 'none') === 'none';
}
